import React, { useContext, useMemo } from 'react';
import {
  createSearchClient,
  SearchApi,
  TokenCreator,
  useAuth,
} from '@amzn/dots-core-ui';
import { createStorageClient, StorageApi } from './clients';

export type ClientContext = {
  search: SearchApi;
  storageApiUrl: string;
} & StorageApi;

export type ClientProviderProps = {
  children: React.ReactNode;
  createToken?: TokenCreator;
  storageApiUrl: string;
};

const ClientContext = React.createContext<ClientContext | undefined>(undefined);

export const ClientProvider = ({
  children,
  createToken = undefined,
  storageApiUrl,
}: ClientProviderProps): JSX.Element => {
  let ct = createToken;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ct = useAuth().createToken;
  } catch (error) {
    // ignored
  }
  if (ct === undefined) {
    throw new Error('no Token Creator provided');
  }
  const storageClient = createStorageClient(storageApiUrl, createToken ?? ct);

  const search = createSearchClient(storageApiUrl, createToken ?? ct);

  const value = useMemo(
    () => ({
      ...storageClient,
      search,
      storageApiUrl,
    }),
    [storageApiUrl, storageClient, search]
  );
  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
};

export const useClient = (): ClientContext => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
